import { useEffect, useRef, type ReactNode } from 'react';

type Props = {
  index: number;
  label: string;
  selected: boolean;
  given: boolean;
  conflict: boolean;
  wrong?: boolean;
  className?: string;
  children: ReactNode;
  onSelect: (index: number) => void;
  onActivate?: (index: number) => void;
  onDirectSelect?: (index: number) => void;
};

export function GridCell({
  index,
  label,
  selected,
  given,
  conflict,
  wrong = false,
  className = '',
  children,
  onSelect,
  onActivate,
  onDirectSelect,
}: Props) {
  const longPressTimer = useRef<number | null>(null);
  const directTriggered = useRef(false);

  const clearLongPress = () => {
    if (longPressTimer.current !== null) window.clearTimeout(longPressTimer.current);
    longPressTimer.current = null;
  };

  useEffect(() => clearLongPress, []);

  return (
    <button
      type="button"
      role="gridcell"
      data-grid-index={index}
      tabIndex={selected ? 0 : -1}
      aria-label={label}
      aria-selected={selected}
      aria-invalid={conflict || wrong || undefined}
      className={`grid-cell ${selected ? 'grid-cell--selected' : ''} ${
        given ? 'grid-cell--given' : 'grid-cell--editable'
      } ${conflict ? 'grid-cell--conflict' : ''} ${wrong ? 'grid-cell--wrong' : ''} ${className}`}
      onClick={() => {
        if (given) return;
        if (directTriggered.current) {
          directTriggered.current = false;
          return;
        }
        (onActivate ?? onSelect)(index);
      }}
      onContextMenu={(event) => {
        if (given || !onDirectSelect) return;
        event.preventDefault();
        onDirectSelect(index);
      }}
      onPointerDown={(event) => {
        if (given || !onDirectSelect || event.pointerType === 'mouse') return;
        clearLongPress();
        directTriggered.current = false;
        longPressTimer.current = window.setTimeout(() => {
          directTriggered.current = true;
          onDirectSelect(index);
        }, 450);
      }}
      onPointerUp={clearLongPress}
      onPointerCancel={clearLongPress}
      onPointerLeave={clearLongPress}
    >
      {children}
    </button>
  );
}
